// client/src/components/CourseDetails.js

import React from 'react';
import { Row, Col, Image, Button } from 'react-bootstrap';
import { FaFlag, FaGolfBall, FaStar } from 'react-icons/fa';
import ExpandedCard from './ExpandedCard';

// --- Helper Functions ---

const getLocalCourseImage = (courseName) => {
  if (!courseName) return;
  const trimmedName = courseName.trim();
  const imageMap = {
    'Lions Municipal Golf Course': '/images/Lions.jpg',
    'Roy Kizer Golf Course': '/images/Kizer.jpg',
    'Jimmy Clay Golf Course': '/images/Clay.jpg',
    'Morris Williams Golf Course': '/images/Morris-Williams.jpg',

  };

  return imageMap[trimmedName];
};

const formatFilterTime = (hour) => `${String(hour).padStart(2, '0')}:00`;

const convertTo24Hour = (timeString) => {
  if (!timeString || !timeString.includes(' ')) return '00:00';
  const [time, modifier] = timeString.split(' ');
  let [hours, minutes] = time.split(':');
  if (hours === '12') hours = '00';
  if (modifier.toLowerCase() === 'pm') hours = parseInt(hours, 10) + 12;
  return `${String(hours).padStart(2, '0')}:${minutes}`;
};

// ✅ ADDED: Helper to check if any tee time is bookable right now
const isAnythingBookableNow = (teeTimes) => {
  if (!teeTimes || teeTimes.length === 0) return false;
  return teeTimes.some(time => time.cartButtonUrl && time.cartButtonUrl.startsWith('http'));
};

const CourseDetails = ({ course, allTeeTimes, filters, onClose, onTimeClick, onScheduleClick, onStandbyClick }) => {
  if (!course) return null;

  const timesToDisplay = (allTeeTimes || []).filter(time => {
    if (!filters) return true;
    const teeTime24 = convertTo24Hour(time.time);
    const startTime = formatFilterTime(filters.startTime);
    const endTime = formatFilterTime(filters.endTime);
    const isAfterStart = teeTime24 >= startTime;
    const isBeforeEnd = teeTime24 <= endTime;
    const hasEnoughSlots = parseInt(time.openSlots) >= filters.golfers;
    return isAfterStart && isBeforeEnd && hasEnoughSlots;
  });

  const localImage = getLocalCourseImage(course.name);
  const imageUrl = course.image_url || localImage || '/images/golf-course-backdrop.jpg';
  const isWindowOpen = isAnythingBookableNow(allTeeTimes);

  const StatItem = ({ icon, value, label }) => (
    <div className="text-center">
      {icon}
      <div className="fw-bold">{value}</div>
      <small className="text-muted">{label}</small>
    </div>
  );

  return (
    <ExpandedCard>
      <div style={{ position: 'absolute', top: 15, right: 20, cursor: 'pointer', fontSize: '1.5rem', zIndex: 10 }} onClick={onClose}>
        ✖
      </div>

      <Row>
        {/* Left Column: Course Details */}
        <Col md={5} className="p-3">
          <Image src={imageUrl} alt={course.name} rounded className="w-100 mb-3" style={{ height: '180px', objectFit: 'cover' }} />

          <div className="d-flex justify-content-around my-3">
            <StatItem icon={<FaFlag className="mb-1" />} value={course.par || 'N/A'} label="Par" />
            <StatItem icon={<FaGolfBall className="mb-1" />} value={course.holes || 'N/A'} label="Holes" />
            <StatItem icon={<FaStar className="mb-1" />} value={course.rating || 'N/A'} label="Rating" />
          </div>

          <hr />

          <div className="mb-3">
            <h6 className="fw-bold">Description</h6>
            <p className="text-muted small">{course.description || 'No description available.'}</p>
          </div>

          <div>
            <h6 className="fw-bold">Amenities</h6>
            <ul className="list-unstyled text-muted small">
              {course.driving_range && <li>• Driving Range</li>}
              {/* Add other boolean amenities here, e.g., course.putting_green */}
              {!course.driving_range && <li>No special amenities listed.</li>}
            </ul>
          </div>
        </Col>

        {/* Right Column: Tee Times */}
        <Col md={7} className="p-4 bg-light rounded-end">
          <h4 className="fw-bold">{course.name}</h4>
          <p className="text-muted mb-3">{course.course_type} Course</p>

          <Row className="g-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {timesToDisplay.length > 0 ? (
              timesToDisplay.map((time, idx) => {
                // ✅ Logic to get the riding price for this specific time
                const priceKey = filters.holes === 9 ? '9_holes' : '18_holes';
                const ridingPrice = time.price?.[priceKey]?.riding;

                return (
                  <Col xs={4} key={idx}>
                    <Button
                      variant={time.canBook ? "light" : "outline-secondary"}
                      className="w-100 py-2 shadow-sm d-flex flex-column"
                      disabled={!time.canBook}
                      onClick={() => onTimeClick(course, time)}
                      style={{ lineHeight: '1.2' }}
                    >
                      <span className="fw-bold">{time.time}</span>
                      <small className="text-muted">{time.openSlots} slots</small>
                      {/* ✅ Display the riding price if it exists */}
                      {ridingPrice && (
                        <small className="text-success fw-bold">${ridingPrice.toFixed(0)} w/cart</small>
                      )}
                    </Button>
                  </Col>
                );
              })
            ) : (
              <Col xs={12}>
                <p className='text-muted fst-italic'>No times available that match your filters.</p>
              </Col>
            )}
          </Row>

          {/* ✅ UPDATED: Footer button is now conditional */}
          <div className="mt-4 text-center">
            <Button
              variant="primary"
              className="w-100"
              onClick={() => isWindowOpen ? onStandbyClick(course) : onScheduleClick(course)}
            >
              {isWindowOpen ? 'Join Standby List' : 'Schedule for a Future Date'}
            </Button>
          </div>
        </Col>
      </Row>
    </ExpandedCard>
  );
};

export default CourseDetails;